<template>
  <div
    class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-white"
  >
    <div class="rounded-t mb-0 px-4 py-3 bg-transparent">
      <div class="flex flex-wrap items-center justify-between">
        <div class="relative w-full max-w-full flex-grow flex-1">
          <h6 class="uppercase text-blueGray-400 mb-1 text-xs font-semibold">
            Overview
          </h6>
          <h2 class="text-blueGray-700 text-xl font-semibold">
            活动创建转化率
          </h2>
        </div>
        <div class="flex items-center gap-2">
          <el-select v-model="compareDays" @change="handleCompareChange" size="small" style="width: 140px;">
            <el-option label="近3天对比" :value="3" />
            <el-option label="近7天对比" :value="7" />
          </el-select>
        </div>
      </div>
    </div>
    <div class="p-4 flex-auto">
      <div class="relative h-350-px">
        <canvas id="funnel-chart"></canvas>
      </div>
    </div>
  </div>
</template>
<script>
import { Chart, registerables } from "chart.js";
import { getAdminApiUrl } from "@/api/luckdraw.js";
import { getUserToken } from "@/utils/auth.js";

// 注册所有Chart.js组件
Chart.register(...registerables);

const COLORS = [
  { bg: '#3b82f6', border: '#3b82f6' },
  { bg: '#10b981', border: '#10b981' },
  { bg: '#f59e0b', border: '#f59e0b' },
  { bg: '#8b5cf6', border: '#8b5cf6' },
  { bg: '#ef4444', border: '#ef4444' },
  { bg: '#ec4899', border: '#ec4899' },
  { bg: '#06b6d4', border: '#06b6d4' }
];

// Demo 数据 - 退出属于少数
function getDemoData() {
  return [
    {
      label: '2月1日',
      color: '#3b82f6',
      data: [1150, 85, 1045] // 进入1150，退出85(7.4%)，完成1045
    },
    {
      label: '1月31日',
      color: '#10b981',
      data: [1080, 92, 970] // 进入1080，退出92(8.5%)，完成970
    }
  ];
}

// 从API获取数据
async function fetchFunnelStats(days) {
  const token = getUserToken();
  const response = await fetch(getAdminApiUrl('/stat/daily/create/funnel'), {
    method: 'POST',
    headers: {
      'Token': `${token}`,
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ days: days })
  });
  const result = await response.json();
  if (result.sta === 0 && result.data) {
    return result.data;
  }
  return null;
}

export default {
  data() {
    return {
      compareDays: 3,
      chart: null,
    };
  },
  mounted: function () {
    this.loadData();
  },
  methods: {
    async loadData() {
      await this.$nextTick();
      const apiData = await fetchFunnelStats(this.compareDays);

      const labels = ['进入创建', '退出创建', '创建完成'];
      let datasets;

      if (apiData && apiData.datasets && apiData.datasets.length > 0) {
        datasets = apiData.datasets.map((ds, idx) => ({
          label: ds.label,
          backgroundColor: ds.color || COLORS[idx % COLORS.length].bg,
          borderColor: ds.color || COLORS[idx % COLORS.length].border,
          data: ds.data,
          borderWidth: 2,
          barPercentage: 0.95,
        }));
      } else {
        // 使用 Demo 数据展示效果
        const demo = getDemoData();
        datasets = demo.map((item) => ({
          label: item.label,
          backgroundColor: item.color,
          borderColor: item.color,
          data: item.data,
          borderWidth: 2,
          barPercentage: 0.95,
        }));
      }

      const config = {
        type: "bar",
        data: {
          labels: labels,
          datasets: datasets,
        },
        options: {
          indexAxis: 'y',
          maintainAspectRatio: false,
          responsive: true,
          plugins: {
            legend: {
              labels: {
                color: "rgba(0,0,0,.6)",
                usePointStyle: true,
              },
              position: 'bottom',
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  const idx = context.datasetIndex;
                  const value = context.raw;
                  const dataset = context.chart.data.datasets[idx];
                  const firstValue = dataset.data[0];
                  const percent = ((value / firstValue) * 100).toFixed(1);
                  return `${dataset.label}: ${value} (${percent}%)`;
                }
              }
            },
            // 在条形后面绘制百分比
            afterDraw: function(chart) {
              const ctx = chart.ctx;
              chart.data.datasets.forEach((dataset, datasetIdx) => {
                const meta = chart.getDatasetMeta(datasetIdx);
                const firstValue = dataset.data[0];

                meta.data.forEach((bar, index) => {
                  const value = dataset.data[index];
                  const percent = ((value / firstValue) * 100).toFixed(1) + '%';

                  ctx.save();
                  ctx.font = 'bold 14px sans-serif';
                  ctx.fillStyle = '#374151';
                  ctx.textAlign = 'left';
                  ctx.textBaseline = 'middle';

                  // 在条形末端绘制百分比
                  const x = bar.x + 10;
                  const y = bar.y;
                  ctx.fillText(percent, x, y);
                  ctx.restore();
                });
              });
            }
          },
          scales: {
            x: {
              display: true,
              grid: {
                color: "rgba(33, 37, 41, 0.1)",
              },
              ticks: {
                callback: function(value) {
                  return value.toLocaleString();
                }
              }
            },
            y: {
              display: true,
              grid: {
                display: false,
              },
            }
          },
        },
      };

      // 销毁旧图表
      if (window.myFunnelChart) {
        window.myFunnelChart.destroy();
      }

      const ctx = document.getElementById("funnel-chart").getContext("2d");
      window.myFunnelChart = new Chart(ctx, config);
    },
    handleCompareChange() {
      this.loadData();
    },
  },
};
</script>
